import type { TClinicBranch, TClinicBranchCityGroup, TClinicBranchesResponse } from '~/types/clinicBranch'
import { resolveClinicRouteAfterBranchSwitch } from '~/utils/clinicBranchSwitchRoute'

const UNKNOWN_CITY = 'Без города'

export type TBranchDropdownSource = 'card' | 'header' | 'menu'

function groupBranchesByCity(branches: TClinicBranch[]): TClinicBranchCityGroup[] {
  const groups = new Map<string, TClinicBranch[]>()

  for (const branch of branches) {
    const cityName = branch.cityName?.trim() || UNKNOWN_CITY
    const list = groups.get(cityName) ?? []
    list.push(branch)
    groups.set(cityName, list)
  }

  return Array.from(groups.entries()).map(([cityName, cityBranches]) => ({
    cityName,
    branches: cityBranches,
  }))
}

export function useClinicBranches() {
  const { apiQuery } = useApi()
  const authStore = useAuthStore()

  const branches = useState<TClinicBranch[]>('clinic-branches', () => [])
  const activeClinicId = useState<number | null>('clinic-active-id', () => null)
  const isLoading = useState('clinic-branches-loading', () => false)
  const isSwitching = useState('clinic-branches-switching', () => false)
  const error = useState<string | null>('clinic-branches-error', () => null)
  const isBranchDropdownOpen = useState('clinic-branch-dropdown-open', () => false)
  const branchDropdownSource = useState<TBranchDropdownSource | null>('clinic-branch-dropdown-source', () => null)
  const branchDropdownListboxId = useState('clinic-branch-listbox-id', () => '')

  const hasMultipleBranches = computed(() => branches.value.length > 1)

  const groups = computed(() => groupBranchesByCity(branches.value))

  const activeBranch = computed(() =>
    branches.value.find((branch) => branch.isActive)
    ?? branches.value.find((branch) => branch.id === activeClinicId.value)
    ?? null,
  )

  async function fetchBranches() {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiQuery<TClinicBranchesResponse>('/clinic/branches', 'GET', {})
      branches.value = response.data.branches
      activeClinicId.value = response.data.activeClinicId
    } catch (e: unknown) {
      branches.value = []
      activeClinicId.value = null
      error.value = (e as { message?: string })?.message ?? 'Не удалось загрузить филиалы'
    } finally {
      isLoading.value = false
    }
  }

  async function switchBranch(clinicId: number) {
    if (isSwitching.value || clinicId === activeClinicId.value) {
      return
    }

    isSwitching.value = true
    error.value = null

    try {
      const response = await apiQuery<{ success: boolean; data: Record<string, unknown> }>(
        '/clinic/branches/switch',
        'POST',
        { body: { clinicId } },
      )

      authStore.setUserProfileData(response.data)
      activeClinicId.value = clinicId
      branches.value = branches.value.map((branch) => ({
        ...branch,
        isActive: branch.id === clinicId,
      }))

      closeBranchDropdown()
      clearNuxtData()

      const route = useRoute()
      const { path: targetPath, clearQuery } = resolveClinicRouteAfterBranchSwitch(route.path)

      if (route.path.startsWith('/profile-clinic')) {
        await navigateTo({
          path: targetPath,
          query: clearQuery ? {} : { ...route.query },
        }, { replace: true })
      }

      await refreshNuxtApp({ force: true })
    } catch (e: unknown) {
      error.value = (e as { message?: string })?.message ?? 'Не удалось переключить филиал'
      throw e
    } finally {
      isSwitching.value = false
    }
  }

  function initBranchDropdownListboxId(id: string) {
    if (!branchDropdownListboxId.value) {
      branchDropdownListboxId.value = id
    }

    return branchDropdownListboxId.value
  }

  function toggleBranchDropdown(source: TBranchDropdownSource) {
    if (isBranchDropdownOpen.value && branchDropdownSource.value === source) {
      closeBranchDropdown()
      return
    }

    branchDropdownSource.value = source
    isBranchDropdownOpen.value = true
  }

  function closeBranchDropdown() {
    isBranchDropdownOpen.value = false
    branchDropdownSource.value = null
  }

  let branchDropdownListenersAttached = false

  function attachBranchDropdownListeners() {
    if (!import.meta.client || branchDropdownListenersAttached) {
      return
    }

    branchDropdownListenersAttached = true

    document.addEventListener('click', (event) => {
      if (!isBranchDropdownOpen.value) {
        return
      }

      const target = event.target
      if (target instanceof Element && target.closest('.clinic-branch-switcher')) {
        return
      }

      closeBranchDropdown()
    })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeBranchDropdown()
      }
    })
  }

  return {
    branches,
    activeClinicId,
    activeBranch,
    groups,
    hasMultipleBranches,
    isLoading,
    isSwitching,
    error,
    isBranchDropdownOpen,
    branchDropdownSource,
    branchDropdownListboxId,
    fetchBranches,
    switchBranch,
    initBranchDropdownListboxId,
    toggleBranchDropdown,
    closeBranchDropdown,
    attachBranchDropdownListeners,
  }
}
